package com.karyadina.web.ui

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.expandVertically
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Cloud
import androidx.compose.material.icons.filled.CloudOff
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.karyadina.cloudsync.service.CloudSyncService
import com.karyadina.cloudsync.service.GoogleAuthService
import com.karyadina.cloudsync.model.SyncStatus
import kotlinx.coroutines.CoroutineStart
import kotlinx.coroutines.launch
import org.koin.compose.koinInject

@Composable
fun WebCloudSyncCard(
    cloudSyncService: CloudSyncService = koinInject(),
    googleAuthService: GoogleAuthService = koinInject()
) {
    val coroutineScope = rememberCoroutineScope()
    val isEnabled by cloudSyncService.syncEnabled.collectAsState()
    val syncStatus by cloudSyncService.syncStatus.collectAsState()
    val lastSyncTime by cloudSyncService.lastSyncTime.collectAsState()
    var syncError by remember { mutableStateOf<String?>(null) }
    
    val isLoading = syncStatus is SyncStatus.Syncing

    Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(12.dp)) {
        // Header
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Icon(
                imageVector = if (isEnabled) Icons.Filled.Cloud else Icons.Filled.CloudOff,
                contentDescription = "Cloud Sync",
                tint = if (isEnabled) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant
            )
            Text(
                text = "Cloud Sync (Web)",
                style = MaterialTheme.typography.titleMedium,
                modifier = Modifier.weight(1f)
            )
        }

        // Main control
        Card(
            modifier = Modifier.fillMaxWidth(),
            colors = CardDefaults.cardColors(
                containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f)
            )
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(12.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = if (isEnabled) "Syncing Enabled" else "Syncing Disabled",
                        style = MaterialTheme.typography.bodyMedium
                    )
                    Text(
                        text = "Backup your data securely to Google Drive",
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                Switch(
                    checked = isEnabled,
                    onCheckedChange = { newState ->
                        coroutineScope.launch(start = CoroutineStart.UNDISPATCHED) {
                            if (newState) {
                                val authResult = googleAuthService.authenticate(null)
                                if (authResult.isSuccess) {
                                    cloudSyncService.enableSync()
                                    syncError = null
                                } else {
                                    syncError = "Authentication failed: ${authResult.exceptionOrNull()?.message}"
                                }
                            } else {
                                cloudSyncService.disableSync()
                            }
                        }
                    },
                    enabled = !isLoading
                )
            }
        }

        // Active state
        AnimatedVisibility(
            visible = isEnabled,
            enter = expandVertically(),
            exit = shrinkVertically()
        ) {
            Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    colors = CardDefaults.cardColors(
                        containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f)
                    )
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(12.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        Column(modifier = Modifier.weight(1f)) {
                            Text("Manual Sync", style = MaterialTheme.typography.bodySmall)
                            Text("Force a snapshot sync immediately", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                            
                            if (lastSyncTime > 0) {
                                Text(
                                    text = "Last sync: ${formatLastSync(lastSyncTime)}",
                                    style = MaterialTheme.typography.labelSmall,
                                    color = MaterialTheme.colorScheme.primary.copy(alpha = 0.7f),
                                    modifier = Modifier.padding(top = 4.dp)
                                )
                            }
                        }
                        IconButton(
                            onClick = {
                                coroutineScope.launch(start = CoroutineStart.UNDISPATCHED) {
                                    cloudSyncService.syncNow()
                                }
                            },
                            enabled = !isLoading
                        ) {
                            Icon(
                                imageVector = Icons.Filled.Refresh,
                                contentDescription = "Sync Now",
                                tint = if (isLoading) MaterialTheme.colorScheme.outlineVariant else MaterialTheme.colorScheme.primary
                            )
                        }
                    }
                }
            }
        }

        // Error message
        if (syncError != null || syncStatus is SyncStatus.Error) {
            val displayError = syncError ?: (syncStatus as? SyncStatus.Error)?.message ?: "Unknown error"
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.errorContainer)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(12.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Icon(Icons.Filled.Warning, "Error", tint = MaterialTheme.colorScheme.error)
                    Text(
                        text = displayError,
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.error,
                        modifier = Modifier.weight(1f)
                    )
                }
            }
        }
    }
}

private fun formatLastSync(timeMs: Long): String {
    if (timeMs <= 0) return "Never"
    val now = com.karyadina.util.currentTimeMillis()
    val diffMs = now - timeMs
    return when {
        diffMs < 60_000 -> "just now"
        diffMs < 3600_000 -> "${diffMs / 60_000}m ago"
        diffMs < 86_400_000 -> "${diffMs / 3600_000}h ago"
        else -> "${diffMs / 86_400_000}d ago"
    }
}
